/**
 * Reschedule and restore share the Visit-only appointment command and refresh the existing pane. Reschedule of a
 * Visit a person approved offers "Keep approved": left on, the move runs as the Reschedule bulk row with
 * `keepApproved` (move, then stamp the moved revisions); off, it is the plain date command and the approval reads Changed.
 */
import { computed, inject } from '@angular/core';
import { ActivityEntityName, BulkActionStatus, heldByPerson, JobSecurityRight, VisitBrowseMode, VisitBrowseRow, VisitBulkActionId, VisitContentSource } from '@jms/model';
import { VISIT_DATES_COMMAND, VisitDatesRequest } from '@jms/model/entities/planned.work/visit/visit.dates';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { WorkBuddyDialogApi, WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { EntityBulkActionExecutor } from '@workbuddy/ui/v2/blocks/entity.bulk.actions';
import { ActionCommandCapability } from '@workbuddy/ui/v2/surface.engine';
import { VisitDatesDialogComponent } from '../dialogs/visit.dates/visit.dates.dialog';
import { VisitDatesDialogOptions, VisitRescheduleChoice, VisitRescheduleOutcome, VisitRescheduleSubmit } from '../dialogs/visit.dates/visit.dates.dialog.model';
import { visitPlanNotStartedReason } from '../visit.model';
import { VisitDatesResult, VisitMoveCommandRequest } from '../visit.movement.types';
import { reportVisitMoveResult } from './visit.action.feedback';

export function visitDatesAction(restore: boolean, id: string, label: string): ActionCommandCapability<VisitBrowseRow> {

  const rights = inject(RightsService);
  const data = inject(EntityDataService);
  const dialogs = inject(WorkBuddyDialogService);
  const toasts = inject(ToastService);
  const executor = inject(EntityBulkActionExecutor);
  const allowed = (row: VisitBrowseRow) => rights.hasRights(JobSecurityRight.Manage) && visitDatesEditable(row) && (!restore || row.datesOverridden === true);

  return {
    descriptor: ({ page }) => computed(() => ({
      id,
      label,
      iconLeft: restore ? 'fa-regular fa-arrow-rotate-left' : 'fa-regular fa-calendar',
      hidden: page.$readonly() || !allowed(page.$model()),
      disabled: page.$saving() || page.$dirty(),
      pin: 'never',
    })),
    run: async page => {
      const row = page.$model();
      if (page.$readonly() || page.$saving() || page.$dirty() || !allowed(row)) return;
      if (restore) {
        const request: VisitDatesRequest = { id: row._id, restore };
        const updated = await data.executeCommand<VisitBrowseRow>(ActivityEntityName.Visit, VISIT_DATES_COMMAND, request);
        if (!updated?._id) throw new Error('The date command did not return the updated Visit');
        page.reset({ ...updated });
        invalidateVisitMove(data);
        return;
      }
      // One id per confirmation: a retry of a lost answer replays the recorded approval instead of approving twice.
      let requestId = crypto.randomUUID();
      const moveKeepingApproval: VisitRescheduleSubmit = async choice => {
        const result = await executor.execute<VisitDatesResult>({
          context: 'record',
          entity: ActivityEntityName.Visit,
          prepared: true,
          args: {
            action: VisitBulkActionId.Reschedule,
            data: [row],
            bulkActionRequest: {
              entity: ActivityEntityName.Visit,
              entityId: row._id,
              actionId: VisitBulkActionId.Reschedule,
              actionLabel: label.replace('…', ''),
              actionData: { recordMove: { ...visitMoveRequest(row, choice), requestId }, keepApproved: true },
            },
          },
          onCompleted: () => {
            invalidateVisitMove(data);
            data.invalidate(ActivityEntityName.WorkTaskSchedule);
          },
        });
        const updated = result?.record;
        // The executor already toasted a failed or refused run; a second, technical error would report it twice.
        if (!updated?._id && result?.status !== BulkActionStatus.Success) return undefined;
        if (!updated?._id) throw new Error('Reschedule did not return the updated Visit');
        // A stale review approved nothing, so the reconfirm is a new request rather than a replay of this answer.
        if (updated.movement?.refreshedPreview) requestId = crypto.randomUUID();
        return updated;
      };
      const submit: VisitRescheduleSubmit = async choice => {
        if (choice.keepApproved) return moveKeepingApproval(choice);
        const request: VisitMoveCommandRequest = {
          ...visitMoveRequest(row, choice),
          restore: false,
          // One id per confirmation: a stale answer records nothing, so the reconfirm needs its own id to move.
          ...(choice.fingerprint ? { operationId: crypto.randomUUID() } : {}),
        };
        const updated = await data.executeCommand<VisitDatesResult>(ActivityEntityName.Visit, VISIT_DATES_COMMAND, request);
        if (!updated?._id) throw new Error('The date command did not return the updated Visit');
        return updated;
      };
      const outcome = await openVisitDatesDialog(dialogs, row, label, { approvable: visitKeepApprovedAvailable(row), submit });
      if (!outcome?.result) return;
      page.reset({ ...outcome.result });
      invalidateVisitMove(data);
      // The bulk executor already reported a move that kept its approval.
      if (!outcome.choice.keepApproved) reportVisitMoveResult(toasts, label, outcome.result.movement);
    },
  };
}

/**
 * The move every reschedule sends, whichever command carries it: the Visit, the picked appointment or the
 * joined Visit, the window confirmation, the booking choice and the reviewed preview's fingerprint.
 */
export function visitMoveRequest(row: VisitBrowseRow, choice: VisitRescheduleChoice): Pick<VisitMoveCommandRequest, 'id' | 'dates' | 'targetVisitId' | 'allowOutsideWindow' | 'fingerprint' | 'booking'> {

  return {
    id: row._id,
    dates: choice.dates,
    ...(choice.targetVisitId ? { targetVisitId: choice.targetVisitId } : {}),
    ...(choice.allowOutsideWindow ? { allowOutsideWindow: true } : {}),
    ...(choice.booking ? { booking: choice.booking } : {}),
    ...(choice.fingerprint ? { fingerprint: choice.fingerprint } : {}),
  };
}

/** A Job-less, live, stored-or-projected Planned attendance of a Started plan; a Job owns its own appointment. */
export function visitDatesEditable(row: VisitBrowseRow): boolean {

  return row.mode === VisitBrowseMode.Operational && !visitPlanNotStartedReason(row) && !row.job?._id && row.active !== false
    && row.contentSource !== VisitContentSource.Snapshot && (!row.visitId || row.status === 'Planned');
}

/**
 * A person approved some of this Visit's work and the approval may be re-stamped after the move: it names a
 * customer and an occurrence and is not under forecast review. An auto-approved line re-approves itself.
 */
export function visitKeepApprovedAvailable(row: VisitBrowseRow): boolean {

  return heldByPerson(row.approvalStatus) && !!row.customer?._id && !!row.occurrenceId && !row.noLongerScheduled;
}

export function openVisitDatesDialog(dialogs: WorkBuddyDialogApi, row: VisitBrowseRow, label: string, options: VisitDatesDialogOptions = {}): Promise<VisitRescheduleOutcome | undefined> {

  return dialogs.modal<VisitRescheduleOutcome, VisitDatesDialogComponent>(VisitDatesDialogComponent, {
    width: 'min(480px, calc(100vw - 40px))',
    align: 'top',
    aria: { label },
    inputs: { row, decision: options.decision === true, approvable: options.approvable === true, submit: options.submit },
  });
}

/** The boards that show a moved Visit or its work. */
export function invalidateVisitMove(data: EntityDataService): void {

  data.invalidateItems(ActivityEntityName.Visit);
  data.invalidateItems(ActivityEntityName.UpcomingWork);
}

/**
 * Settles a passed appointment before construction: the prefilled current window confirms it, a new
 * one moves it. The server stamps the decision; construction refuses the passed date until it has one.
 * Resolves null when the person backs out, so nothing is written.
 */
export async function decideVisitAppointment(dialogs: WorkBuddyDialogApi, data: EntityDataService, row: VisitBrowseRow): Promise<(() => Promise<VisitBrowseRow>) | null> {

  const outcome = await openVisitDatesDialog(dialogs, row, 'Confirm appointment', { decision: true });
  if (!outcome) return null;
  const choice = outcome.choice;
  return async () => {
    // No fingerprint: the move runs after the dispatch reason, so it is all-or-nothing rather than a reviewed partial.
    const request: VisitMoveCommandRequest = {
      ...visitMoveRequest(row, { ...choice, fingerprint: undefined }),
      restore: false,
    };
    const updated = await data.executeCommand<VisitBrowseRow>(ActivityEntityName.Visit, VISIT_DATES_COMMAND, request);
    if (!updated?._id) throw new Error('The date command did not return the updated Visit');
    return updated;
  };
}
